import React, { useMemo, useState } from 'react';
import SearchInput from '@/components/SearchInput';
import { EXAMPLE_PROMPTS, shuffleArray } from '@/lib/constants';
import type { ActionType } from '@/lib/types';
import polemiaLogo from '@/assets/polemia-logo-hero.png';

interface HeroSectionProps {
  question: string;
  onQuestionChange: (q: string) => void;
  isLoading: boolean;
  loadingType: ActionType | null;
  answerSize: number;
  onAnswerSizeChange: (size: number) => void;
  onSearch: () => void;
  onAnalyze: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  question,
  onQuestionChange,
  isLoading,
  loadingType,
  answerSize,
  onAnswerSizeChange,
  onSearch,
  onAnalyze,
}) => {
  const [showAllPrompts, setShowAllPrompts] = useState(false);
  
  // Shuffle prompts once on mount
  const shuffledPrompts = useMemo(() => shuffleArray(EXAMPLE_PROMPTS), []);
  const visiblePrompts = showAllPrompts ? shuffledPrompts : shuffledPrompts.slice(0, 6);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-12">
      {/* Logo */}
      <a href="https://ia.polemia.com" className="block mb-6 sm:mb-8">
        <img
          src={polemiaLogo}
          alt="IA Polemia"
          className="h-12 sm:h-14 object-contain"
        />
      </a>
      
      {/* Catchy title */}
      <h1 className="text-xl sm:text-2xl lg:text-3xl font-light text-foreground mb-6 sm:mb-8 text-center">
        Que voulez-vous explorer ?
      </h1>
      
      {/* Search Bar */}
      <div className="w-full max-w-3xl">
        <SearchInput
          value={question}
          onChange={onQuestionChange}
          isLoading={isLoading}
          loadingType={loadingType || undefined}
          onSearch={onSearch}
          onAnalyze={onAnalyze}
          answerSize={answerSize}
          onAnswerSizeChange={onAnswerSizeChange}
        />
      </div>
      
      {/* Example prompts */}
      <div className="flex flex-wrap justify-center gap-2 mt-6 sm:mt-8 w-full max-w-3xl px-2">
        {visiblePrompts.map((prompt, index) => (
          <button
            key={index}
            onClick={() => onQuestionChange(prompt)}
            className="text-xs text-left text-muted-foreground sm:hover:text-primary px-3 py-1.5 rounded-full bg-secondary/40 sm:hover:bg-secondary/70 transition-colors duration-200 cursor-pointer"
          >
            {prompt}
          </button>
        ))}
        <button
          onClick={() => setShowAllPrompts(!showAllPrompts)}
          className="text-xs text-primary sm:hover:text-primary/80 px-3 py-1.5 rounded-full bg-primary/10 sm:hover:bg-primary/20 transition-colors duration-200 cursor-pointer font-medium"
        >
          {showAllPrompts ? 'Voir moins' : `+${shuffledPrompts.length - 6}`}
        </button>
      </div>
    </div>
  );
};

export default HeroSection;
